import React, { useState } from "react";
import ConfirmDeleteModal from "./ConfirmDeleteModal";

interface DeleteCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (numCode: number) => void;
  isLoading?: boolean;
  maxCode?: number;
}

const DeleteCodesModal: React.FC<DeleteCodesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  maxCode = 100000,
}) => {
  const [numCode, setNumCode] = useState<string>("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string>("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const num = parseInt(numCode) || 0;
    if (num >= 1 && num <= maxCode) {
      setError("");
      setShowConfirm(true);
    } else if (num < 1) {
      setError("Vui lòng nhập số lượng code cần xóa (ít nhất 1)");
    } else if (num > maxCode) {
      setError(`Số lượng code chỉ có ${maxCode} code chưa sử dụng`);
    }
  };

  const handleConfirmDelete = () => {
    const num = parseInt(numCode) || 0;
    onSubmit(num);
    setShowConfirm(false);
  };

  const handleClose = () => {
    if (!isLoading) {
      setNumCode("");
      setShowConfirm(false);
      setError("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Xóa mã code</h3>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-4">
          <div className="mb-4">
            <label
              htmlFor="numCode"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Số lượng mã code cần xóa
            </label>
            <input
              type="number"
              id="numCode"
              min="1"
              max={maxCode}
              value={numCode}
              onChange={(e) => {
                setNumCode(e.target.value);
                setError("");
              }}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                error ? "border-red-300 dark:border-red-600" : "border-gray-300 dark:border-gray-600"
              }`}
              disabled={isLoading}
              required
              placeholder={`Nhập số lượng code cần xóa (tối đa ${maxCode})`}
            />
            {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
            {numCode && (parseInt(numCode) || 0) > maxCode && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Hiện tại chỉ có {maxCode} code chưa sử dụng
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={
                isLoading ||
                (parseInt(numCode) || 0) < 1 ||
                (parseInt(numCode) || 0) > maxCode
              }
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 dark:hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Đang xóa...
                </div>
              ) : (
                "Xóa mã code"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmDelete}
        numCode={parseInt(numCode) || 0}
        isLoading={isLoading}
      />
    </div>
  );
};

export default DeleteCodesModal;
